/** The only dev cluster. Every other region is a production deployment. */
const DEV_REGION = 'eu-central-dev';

/**
 * Whether we are running against the dev cluster, or on a developer's machine.
 *
 * Reads `MOVEO_REGION` — the cluster id the chart injects, and the same value
 * services send to customers as `X-Moveo-Region`. It is empty when running
 * locally, since nothing sets it outside the cluster.
 *
 * This used to substring-match `window.location.hostname` for 'dev' and
 * 'local'. That returns the right answer for every region we run today, but
 * only by luck — it asks the hostname a question the hostname does not answer.
 * A region or a customer domain containing either substring would silently
 * point the console at dev infrastructure.
 */
export const isDevOrLocal = () => !window.MOVEO_REGION || window.MOVEO_REGION === DEV_REGION;

/**
 * Returns the URL for webhooks based on the current environment.
 *
 * Webhooks is a single global service, not a per-region one: every production
 * region shares `webhooks.moveo.ai`, and only dev differs. So this stays a
 * two-way split rather than a per-region lookup.
 *
 * @returns {string} - The webhooks URL.
 */
export const getWebviewsUrl = () =>
  isDevOrLocal() ? 'https://webhooks.dev.moveo.ai' : 'https://webhooks.moveo.ai';

const VARIABLES_FOR_SLOW_QUERIES = [
  'minNumUserMessages',
  'channels',
  'integrationIds',
  'brainVersions',
  'agentIds',
];

/**
 * Returns true if the analytics query to use should be based on the old/slow model.
 * New/Fast queries are based on the presence of a timescale database.
 *
 * @param {Object} variables - Variables used in the analytics query.
 * @param {Object} [options] - Additional options for analytics.
 * @returns {boolean} - True if old query model is used, otherwise false.
 */
export const shouldUseSlowQueries = (
  variables,
  options?: { is_brain_effectiveness?: boolean }
): boolean => {
  // Check the variables used in the query
  const usedVariables = Object.keys(variables);
  if (VARIABLES_FOR_SLOW_QUERIES.some((sv) => usedVariables.includes(sv))) {
    return true;
  }

  if (options?.is_brain_effectiveness && 'isTest' in variables) {
    return true;
  }

  return false;
};
